import { useState } from "react";
import { accountService } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import { createUUID, extractDigits } from "@miplatform/utils";

const getContactUUID = createUUID();
const editContactUUID = createUUID();

/**
 * Custom hook for managing contact details view model.
 *
 * @param {Object} config - Configuration object for the hook.
 * @param {Object} config.edit - The edit object for managing contact details.
 *
 * @returns {Object} Hook return values.
 * @returns {boolean} isLoading - Indicates if the operation is in progress.
 * @returns {boolean} isEditing - Indicates if the contact is in edit mode.
 * @returns {Object} contact - The contact object containing contact information.
 * @returns {Function} getContact - Function to fetch contact details by account ID and contact ID.
 * @returns {Function} updateContact - Function to update the contact details.
 * @returns {Function} handleEditOn - Function to enable edit mode.
 * @returns {Function} handleEditOff - Function to disable edit mode and save changes.
 *
 * @example
 * const {
 *   isLoading,
 *   isEditing,
 *   contact,
 *   getContact,
 *   updateContact,
 *   handleEditOn,
 *   handleEditOff,
 * } = useContactDetailsViewModel({ edit: contactEditObject });
 */
export const useContactDetailsViewModel = ({ edit }) => {
  const stateManager = useComponentState();
  const isLoading = stateManager.isBusy();
  const [contact, setContact] = useState({});
  const [isEditing, setIsEditing] = useState(false);

  const handleEditOn = () => setIsEditing(true);

  const handleEditOff = () => {
    edit.update(contact);
    setIsEditing(false);
  };

  const getContact = ({ contactId }) => {
    stateManager.abort(getContactUUID);
    stateManager.run(async () => {
      const response = await accountService.contacts.getById({ id: contactId });
      if (response.status === HTTP_STATUSES.OK) {
        setContact(response.data);
        edit.update(response.data);
      }
    }, getContactUUID);
  };

  const updateContact = () => {
    stateManager.abort(editContactUUID);
    stateManager.run(async () => {
      const response = await accountService.contacts.update({
        id: contact.id,
        data: {
          ...edit.getValue(),
          accountId: contact.accountId,
          phone: extractDigits(edit.getValue("phone")),
        },
        toastMessageConfig: {
          success: {
            text: `Contact was updated successfully.`,
          },
        },
      });

      if (response.status === HTTP_STATUSES.OK) {
        setContact(response.data);
        edit.update(response.data);
        setIsEditing(false);
      }
    }, editContactUUID);
  };

  return {
    isLoading,
    isEditing,
    contact,
    getContact,
    updateContact,
    handleEditOn,
    handleEditOff,
  };
};
